import { useState, useEffect } from "react";
import { Sidebar, PageHeader } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, 
  Clock, 
  Lock,
  ChevronRight,
  PiggyBank,
  TrendingDown,
  Shield,
  Lightbulb,
  Crown,
  Sparkles,
  AlertCircle,
  Target
} from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORY_ICONS = {
  budgeting: PiggyBank,
  debt: TrendingDown,
  savings: Shield,
  advanced: Lightbulb
};

const CATEGORY_COLORS = {
  budgeting: 'bg-amber-100 text-amber-600 border-amber-200',
  debt: 'bg-red-100 text-red-600 border-red-200',
  savings: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  advanced: 'bg-purple-100 text-purple-600 border-purple-200'
};

export default function EducationPage() {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch recommendations and all content in parallel
        const [recsResponse, contentResponse] = await Promise.all([
          axios.get(`${API}/education/recommendations`, { withCredentials: true }),
          axios.get(`${API}/education`, { withCredentials: true })
        ]);
        
        setRecommendations(recsResponse.data.recommendations || []);
        setContext(recsResponse.data.context);
        setContent(contentResponse.data.content || []);
      } catch (error) {
        console.error('Error fetching education content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchArticle = async (contentId) => {
    try {
      const response = await axios.get(`${API}/education/${contentId}`, { withCredentials: true });
      setSelectedArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    }
  };

  const categories = ['all', ...new Set(content.map(c => c.category))];
  
  const filteredContent = filter === 'all' 
    ? content 
    : content.filter(c => c.category === filter);

  const getCategoryIcon = (category) => CATEGORY_ICONS[category] || BookOpen;
  const getCategoryColor = (category) => CATEGORY_COLORS[category] || 'bg-p2p-teal/10 text-p2p-teal border-p2p-teal/20';

  // Article detail view
  if (selectedArticle) {
    return (
      <Sidebar>
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            className="mb-6 text-p2p-muted hover:text-p2p-teal"
            onClick={() => setSelectedArticle(null)}
            data-testid="back-to-articles-btn"
          >
            ← Back to articles
          </Button>

          <article className="bg-white rounded-2xl border border-p2p-border shadow-sm p-8">
            {selectedArticle.locked ? (
              <div className="text-center py-8">
                <Lock className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
                <h2 className="font-manrope text-2xl font-bold text-p2p-teal mb-2">{selectedArticle.title}</h2>
                <p className="text-p2p-muted mb-6">This content is available to premium subscribers</p>
                <Link to="/settings">
                  <Button className="btn-gold rounded-full">
                    <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase border ${getCategoryColor(selectedArticle.category)}`}>
                    {selectedArticle.category}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-p2p-muted">
                    <Clock className="w-4 h-4" />
                    {selectedArticle.read_time} min read
                  </span>
                </div>

                <h1 className="font-manrope text-3xl font-bold text-p2p-teal mb-4">{selectedArticle.title}</h1>
                <p className="text-lg text-p2p-muted mb-8">{selectedArticle.summary}</p>

                <div className="prose max-w-none">
                  {selectedArticle.content.split('\n\n').map((paragraph, index) => {
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h3 key={index} className="font-manrope text-xl font-semibold text-p2p-teal mt-6 mb-3">
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      );
                    }
                    if (paragraph.startsWith('- ')) {
                      const items = paragraph.split('\n').filter(l => l.startsWith('- '));
                      return (
                        <ul key={index} className="list-disc list-inside space-y-2 text-p2p-muted mb-4">
                          {items.map((item, i) => (
                            <li key={i}>{item.substring(2)}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (paragraph.includes('\n')) {
                      return (
                        <div key={index} className="mb-4">
                          {paragraph.split('\n').map((line, i) => {
                            if (line.startsWith('**')) {
                              return (
                                <h4 key={i} className="font-semibold text-amber-600 mt-4 mb-2">
                                  {line.replace(/\*\*/g, '')}
                                </h4>
                              );
                            }
                            return <p key={i} className="text-p2p-muted">{line}</p>;
                          })}
                        </div>
                      );
                    }
                    return (
                      <p key={index} className="text-p2p-muted mb-4 leading-relaxed">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              </>
            )}
          </article>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <PageHeader 
        title="Financial Education"
        subtitle="Learn the fundamentals of personal finance"
      />

      {/* Contextual Recommendations */}
      {recommendations.length > 0 && (
        <div className="mb-8" data-testid="recommendations-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-p2p-teal to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-manrope text-lg font-semibold text-p2p-teal">Recommended for You</h2>
              <p className="text-sm text-p2p-muted">Based on your financial situation</p>
            </div>
          </div>
          
          {/* Context indicators */}
          {context && (
            <div className="flex flex-wrap gap-2 mb-4">
              {context.has_active_debt && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200">
                  <TrendingDown className="w-3 h-3" />
                  Active Debt
                </span>
              )}
              {context.has_shortfall && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">
                  <AlertCircle className="w-3 h-3" />
                  Budget Tight
                </span>
              )}
              {context.low_safe_to_spend && !context.has_shortfall && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                  <Target className="w-3 h-3" />
                  Building Buffer
                </span>
              )}
            </div>
          )}
          
          <div className="grid md:grid-cols-3 gap-4">
            {recommendations.map((article) => {
              const CategoryIcon = getCategoryIcon(article.category);
              return (
                <div 
                  key={article.content_id}
                  className="bg-gradient-to-br from-p2p-teal/5 to-emerald-50 p-5 rounded-2xl border-2 border-p2p-teal/20 cursor-pointer group hover:border-p2p-teal/40 hover:shadow-lg hover:shadow-p2p-teal/10 transition-all"
                  onClick={() => fetchArticle(article.content_id)}
                  data-testid={`recommendation-${article.content_id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getCategoryColor(article.category)}`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    {article.locked && (
                      <Crown className="w-4 h-4 text-amber-500 ml-auto" />
                    )}
                  </div>
                  <h3 className="font-semibold text-p2p-teal group-hover:text-p2p-teal/80 transition-colors mb-1">
                    {article.title}
                  </h3>
                  <p className="text-sm text-p2p-muted line-clamp-2 mb-3">{article.summary}</p>
                  
                  {/* Reason badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-p2p-teal font-medium bg-p2p-teal/10 px-2 py-1 rounded-full">
                      {article.reason}
                    </span>
                    <span className="text-xs text-p2p-muted flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {article.read_time} min
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Category Filters */}
      <div className="flex gap-2 flex-wrap mb-6">
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={filter === cat ? 'default' : 'outline'}
            size="sm"
            className={filter === cat 
              ? 'bg-p2p-teal text-white rounded-full' 
              : 'border-p2p-border text-p2p-muted hover:bg-p2p-teal/5 rounded-full'
            }
            onClick={() => setFilter(cat)}
            data-testid={`filter-${cat}-btn`}
          >
            {cat === 'all' ? 'All Topics' : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </Button>
        ))}
      </div>

      {/* All Articles Header */}
      <h3 className="font-manrope text-lg font-semibold text-p2p-teal mb-4">
        {filter === 'all' ? 'All Articles' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Articles`}
      </h3>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-teal"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredContent.map((article) => {
            const CategoryIcon = getCategoryIcon(article.category);
            return (
              <div 
                key={article.content_id}
                className="bg-white p-5 rounded-2xl border border-p2p-border/50 shadow-sm cursor-pointer group hover:border-p2p-teal/30 hover:shadow-md transition-all"
                onClick={() => fetchArticle(article.content_id)}
                data-testid={`article-${article.content_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${getCategoryColor(article.category)}`}>
                    <CategoryIcon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-p2p-teal group-hover:text-p2p-teal/80 transition-colors">
                        {article.title}
                      </h3>
                      {article.is_premium && !user?.is_premium && (
                        <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-p2p-muted line-clamp-2">{article.summary}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className="text-xs text-p2p-muted flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {article.read_time} min
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize border ${getCategoryColor(article.category)}`}>
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-p2p-muted group-hover:text-p2p-teal group-hover:translate-x-1 transition-all flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && filteredContent.length === 0 && (
        <div className="bg-white p-12 rounded-2xl border border-p2p-border text-center">
          <BookOpen className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
          <h3 className="font-manrope text-xl font-semibold text-p2p-teal mb-2">No articles found</h3>
          <p className="text-p2p-muted">Try selecting a different category</p>
        </div>
      )}
    </Sidebar>
  );
}
